/// <reference path="../../types/newrelic-video-html5.d.ts" />
import * as Html5TrackerModule from "@newrelic/video-html5";
import type { Html5TrackerLike } from "../../utils/newRelicHtml5Tracker";
import {
  buildHtml5TrackerSignature,
  getHtml5TrackerConstructor,
  disposeHtml5Tracker as utilsDisposeHtml5Tracker,
  findVideoInShadowDOM as utilsFindVideoInShadowDOM,
} from "../../utils/newRelicHtml5Tracker";
import { resolveApiVideoMeta, resolveLivestreamStatus } from "../../utils/fireworkVideoMeta";
import type { VideoDetails } from "./storyBlockTypes";

/**
 * Try to initialize New Relic Html5Tracker for a given firework element.
 * Returns `true` when tracker initialized, `false` when video element not found
 * (callers may retry). Any internal errors are logged via `nr` and `false` is returned.
    } catch (err) {
      nr.error("initHtml5Tracker: Html5TrackerCtor threw during construction", err as Error);
      return false;
    }
    // Instrumentation: inspect tracker instance and New Relic API availability
    try {
      const trackerInstance = refs.videoTrackerRef.current;
      nr.log("initHtml5Tracker: tracker instance created", {
        trackerType: typeof trackerInstance,
        trackerKeys: Object.keys(trackerInstance || {}),
      });

      const nrGlobal = (window as unknown as { newrelic?: Record<string, unknown> }).newrelic;
      nr.log("initHtml5Tracker: window.newrelic presence", { hasNewRelic: Boolean(nrGlobal) });
      if (nrGlobal) {
        try {
          nr.log("initHtml5Tracker: window.newrelic keys", Object.keys(nrGlobal));
        } catch (_e) {
          // ignore
        }
      }

      // If New Relic API present, try a non-invasive test call to confirm agent connectivity.
      if (nrGlobal && typeof nrGlobal.addPageAction === "function") {
        try {
          (nrGlobal.addPageAction as Function)("BFL_NR_DEBUG_TRACKER_INIT", {
            videoId: refs.currentVideoIdRef.current || videoIdFallback || "NA",
            title: refs.currentVideoTitleRef.current || "NA",
          });
          nr.log("initHtml5Tracker: called newrelic.addPageAction for debug");
        } catch (err) {
          nr.error("initHtml5Tracker: newrelic.addPageAction threw", err as Error);
        }
      } else {
        nr.log("initHtml5Tracker: newrelic.addPageAction not available on window.newrelic");
      }
    } catch (e) {
      nr.error("initHtml5Tracker: instrumentation failed", e as Error);
    }
 */
export function initHtml5Tracker(
  fireworkElement: HTMLElement,
  refs: {
    videoTrackerRef: React.MutableRefObject<unknown>;
    lastTrackedVideoElRef: React.MutableRefObject<HTMLVideoElement | null>;
    trackerInitSignatureRef: React.MutableRefObject<string>;
    currentVideoTitleRef: React.MutableRefObject<string>;
    currentVideoIdRef: React.MutableRefObject<string>;
    currentLiveStreamStatusRef: React.MutableRefObject<string>;
  },
  nr: { log: (...args: unknown[]) => void; error: (...args: unknown[]) => void },
  videoIdFallback?: string,
  info?: Record<string, unknown>
) {
  if (typeof window === "undefined") {
    nr.log("Skipping Html5Tracker init: no window available");
    return false;
  }
  if (!window.newrelic) {
    nr.log("Skipping Html5Tracker init: New Relic browser agent unavailable");
    return false;
  }

  nr.log("initHtml5Tracker: attempting to find video element on fireworkElement", fireworkElement);
  const videoElement = utilsFindVideoInShadowDOM(fireworkElement as Element);
  if (!videoElement) {
    nr.log("initHtml5Tracker: video element not found on this fireworkElement");
    // signal to caller that video element is not yet present so it can retry
    return false;
  }

  nr.log("initHtml5Tracker: found video element", {
    tag: videoElement.tagName,
    src: videoElement.currentSrc || videoElement.src,
    duration: videoElement.duration,
  });

  try {
    const nextSignature = buildHtml5TrackerSignature({
      videoElement,
      contentTitle: refs.currentVideoTitleRef?.current,
      videoId: refs.currentVideoIdRef?.current,
      liveStreamStatus: refs.currentLiveStreamStatusRef?.current,
    } as unknown as Parameters<typeof buildHtml5TrackerSignature>[0]);

    if (
      refs.videoTrackerRef.current &&
      refs.lastTrackedVideoElRef.current === videoElement &&
      refs.trackerInitSignatureRef.current === nextSignature
    ) {
      nr.log("initHtml5Tracker: tracker already initialized with same signature, skipping");
      return true;
    }

    if (refs.videoTrackerRef.current) {
      utilsDisposeHtml5Tracker(
        refs.videoTrackerRef.current as Html5TrackerLike | null | undefined,
        nr,
        {
          reason: "reinitialize-existing-tracker",
          logPrefix: "[NR HTML5 Agent]",
        }
      );
    }

    nr.log("initHtml5Tracker: resolving Html5Tracker constructor", {
      moduleKeys: Object.keys(Html5TrackerModule || {}),
      hasWindowNewrelic: Boolean(window?.newrelic),
    });
    const Html5TrackerCtor = getHtml5TrackerConstructor(
      Html5TrackerModule as unknown as unknown,
      nr,
      "[NR HTML5 Agent]"
    );
    if (!Html5TrackerCtor) {
      nr.error("initHtml5Tracker: Html5Tracker constructor not found");
      return false;
    }

    try {
      nr.log("initHtml5Tracker: using info for tracker", info);
      refs.videoTrackerRef.current = new Html5TrackerCtor(videoElement, {
        src: () => videoElement.currentSrc ?? videoElement.src ?? "firework-stream",
        title: () => refs.currentVideoTitleRef.current,
        duration: () => (videoElement.duration as number) ?? 0,
        playhead: () => (videoElement.currentTime as number) ?? 0,
        customData: {
          contentTitle: refs.currentVideoTitleRef.current,
          videoId: refs.currentVideoIdRef.current || videoIdFallback || "NA",
          liveStreamStatus: refs.currentLiveStreamStatusRef.current,
        },
        info: info || {},
      });
    } catch (err) {
      nr.error("initHtml5Tracker: Html5TrackerCtor threw during construction", err as Error);
      return false;
    }
    refs.lastTrackedVideoElRef.current = videoElement;
    refs.trackerInitSignatureRef.current = nextSignature;
    nr.log("initHtml5Tracker: initialized tracker", { videoElement, signature: nextSignature });
    return true;
  } catch (error: unknown) {
    nr.error("Html5Tracker initialization failed", error as Error);
    return false;
  }
}

export function cleanupHtml5Tracker(
  refs: {
    videoTrackerRef: React.MutableRefObject<unknown>;
    lastTrackedVideoElRef: React.MutableRefObject<HTMLVideoElement | null>;
    trackerInitSignatureRef: React.MutableRefObject<string>;
    currentVideoIdRef: React.MutableRefObject<string>;
    currentLiveStreamStatusRef: React.MutableRefObject<string>;
  },
  nr: { log: (...args: unknown[]) => void; error: (...args: unknown[]) => void }
) {
  try {
    utilsDisposeHtml5Tracker(
      refs.videoTrackerRef.current as Html5TrackerLike | null | undefined,
      nr,
      { reason: "component-unmount", logPrefix: "[NR HTML5 Agent]" }
    );
  } catch (e) {
    // ignore
  }
  refs.videoTrackerRef.current = null;
  refs.lastTrackedVideoElRef.current = null;
  refs.trackerInitSignatureRef.current = "";
  refs.currentVideoIdRef.current = refs.currentVideoIdRef.current || "NA";
  refs.currentLiveStreamStatusRef.current = "idle";
}
